/**
 * Transform user CSV data into the format each chart preview expects.
 * Falls through gracefully — returns empty array if mapping is incomplete.
 */

function computeBoxStats(values) {
  if (!values.length) return { median: 0, q1: 0, q3: 0, min: 0, max: 0, outliers: [] };

  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;

  const median = n % 2 === 0
    ? (sorted[n / 2 - 1] + sorted[n / 2]) / 2
    : sorted[Math.floor(n / 2)];

  const lowerHalf = sorted.slice(0, Math.floor(n / 2));
  const upperHalf = sorted.slice(Math.ceil(n / 2));

  const q1 = lowerHalf.length % 2 === 0
    ? (lowerHalf[lowerHalf.length / 2 - 1] + lowerHalf[lowerHalf.length / 2]) / 2
    : lowerHalf[Math.floor(lowerHalf.length / 2)];

  const q3 = upperHalf.length % 2 === 0
    ? (upperHalf[upperHalf.length / 2 - 1] + upperHalf[upperHalf.length / 2]) / 2
    : upperHalf[Math.floor(upperHalf.length / 2)];

  const iqr = q3 - q1;
  const lowerFence = q1 - 1.5 * iqr;
  const upperFence = q3 + 1.5 * iqr;

  const outliers = sorted.filter(v => v < lowerFence || v > upperFence);
  const inRange = sorted.filter(v => v >= lowerFence && v <= upperFence);

  return {
    median,
    q1,
    q3,
    min: inRange.length ? inRange[0] : sorted[0],
    max: inRange.length ? inRange[inRange.length - 1] : sorted[sorted.length - 1],
    outliers,
  };
}

/**
 * Pivot rows into wide format for grouped/stacked bar charts.
 * Input: flat rows with category, value, and series columns.
 * Output: [{ name: 'Cat A', SeriesVal1: num, SeriesVal2: num, ... }]
 * Also returns seriesKeys: the ordered list of unique series values.
 */
function pivotForGrouped(rawData, mapping, prefix) {
  const catCol = mapping.category;
  const valCol = mapping.value;
  const serCol = mapping.series;

  if (!catCol || !valCol || !serCol) return { data: [], seriesKeys: [] };

  // Collect unique series values in order of appearance
  const seriesSet = new Set();
  rawData.forEach(row => {
    const s = row[serCol];
    if (s !== null && s !== undefined) seriesSet.add(String(s));
  });
  const seriesValues = Array.from(seriesSet);

  // Build key map: series value → column key (e.g. "series1", "series2")
  const keyMap = {};
  seriesValues.forEach((sv, i) => {
    keyMap[sv] = `${prefix}${i + 1}`;
  });
  const seriesKeys = seriesValues.map((_, i) => `${prefix}${i + 1}`);

  // Also build a label map: seriesKey → original series value (for legend)
  const labelMap = {};
  seriesValues.forEach((sv, i) => {
    labelMap[`${prefix}${i + 1}`] = sv;
  });

  // Group by category
  const groups = {};
  rawData.forEach(row => {
    const cat = String(row[catCol] ?? '');
    const ser = String(row[serCol] ?? '');
    const val = Number(row[valCol]) || 0;

    if (!groups[cat]) {
      groups[cat] = { name: cat };
      // Initialize all series keys to 0
      seriesKeys.forEach(k => { groups[cat][k] = 0; });
    }
    if (keyMap[ser]) {
      groups[cat][keyMap[ser]] = val;
    }
  });

  return { data: Object.values(groups), seriesKeys, labelMap };
}

function transformWideSeriesRows(rawData, mapping) {
  const xCol = mapping.x;
  const valueColumns = Array.isArray(mapping.yColumns) ? mapping.yColumns : [];
  if (!xCol || valueColumns.length === 0) return [];

  return rawData.flatMap((row) => valueColumns.map((columnName) => ({
    x: Number(row[xCol]) || 0,
    y: Number(row[columnName]) || 0,
    series: columnName,
  })));
}

function transformWideCategoryRows(rawData, mapping, prefix) {
  const categoryCol = mapping.category;
  const valueColumns = Array.isArray(mapping.valueColumns) ? mapping.valueColumns : [];
  if (!categoryCol || valueColumns.length === 0) return [];

  const seriesKeys = valueColumns.map((_, index) => `${prefix}${index + 1}`);
  const labelMap = Object.fromEntries(valueColumns.map((columnName, index) => [`${prefix}${index + 1}`, columnName]));
  const data = rawData.map((row) => {
    const item = { name: String(row[categoryCol] ?? '') };
    valueColumns.forEach((columnName, index) => {
      item[`${prefix}${index + 1}`] = Number(row[columnName]) || 0;
    });
    return item;
  });
  data._seriesKeys = seriesKeys;
  data._labelMap = labelMap;
  return data;
}

function inferRadarAxisColumns(rawData, seriesCol) {
  const firstRow = rawData[0] || {};
  return Object.keys(firstRow).filter((columnName) => {
    if (columnName === seriesCol) return false;
    const nonEmptyValues = rawData
      .map((row) => row[columnName])
      .filter((value) => value !== null && value !== undefined && String(value).trim() !== '');
    if (nonEmptyValues.length === 0) return false;
    return nonEmptyValues.every((value) => !isNaN(Number(value)));
  });
}

/**
 * Counts rows with missing/empty values in the mapped columns.
 * Returns { skippedCount, totalCount } or null if not applicable.
 */
export function countSparseRows(rawData, mapping, chartTypeId) {
  if (!rawData?.length || !mapping) return null;

  let valueCols = [];
  if (['vertical-bar', 'horizontal-bar', 'lollipop', 'diverging-bar'].includes(chartTypeId)) {
    valueCols = [mapping.value].filter(Boolean);
  } else if (['grouped-bar', 'horizontal-grouped-bar', 'stacked-bar', 'horizontal-stacked-bar'].includes(chartTypeId)) {
    valueCols = [mapping.value].filter(Boolean);
    if (Array.isArray(mapping.valueColumns)) valueCols = mapping.valueColumns;
  } else if (['line-chart', 'scatter-plot'].includes(chartTypeId)) {
    valueCols = [mapping.x, mapping.y].filter(Boolean);
  } else if (chartTypeId === 'histogram') {
    valueCols = [mapping.value].filter(Boolean);
  } else if (chartTypeId === 'box-plot') {
    valueCols = [mapping.value].filter(Boolean);
  } else if (chartTypeId === 'heatmap') {
    valueCols = [mapping.value].filter(Boolean);
  } else if (chartTypeId === 'combo') {
    valueCols = [mapping.barValue, mapping.lineValue].filter(Boolean);
  }
  if (valueCols.length === 0) return null;

  let skipped = 0;
  for (const row of rawData) {
    const hasBlank = valueCols.some(col => {
      const v = row[col];
      return v === null || v === undefined || String(v).trim() === '' || isNaN(Number(v));
    });
    if (hasBlank) skipped++;
  }
  if (skipped === 0) return null;
  return { skippedCount: skipped, totalCount: rawData.length };
}

export default function transformData(rawData, mapping, chartTypeId) {
  if (!rawData || !rawData.length || !mapping) return [];

  switch (chartTypeId) {
    case 'vertical-bar':
    case 'horizontal-bar':
    case 'lollipop':
    case 'diverging-bar': {
      const catCol = mapping.category;
      const valCol = mapping.value;
      const colorByCol = mapping.colorBy;
      if (!catCol || !valCol) return [];
      return rawData.map(row => ({
        name: String(row[catCol] ?? ''),
        value: Number(row[valCol]) || 0,
        ...(colorByCol ? { _colorBy: String(row[colorByCol] ?? '') } : {}),
      }));
    }

    case 'grouped-bar':
    case 'horizontal-grouped-bar': {
      if (mapping.inputShape === 'wide' && Array.isArray(mapping.valueColumns)) {
        return transformWideCategoryRows(rawData, mapping, 'series');
      }
      const result = pivotForGrouped(rawData, mapping, 'series');
      // Attach seriesKeys and labelMap as properties on the array
      const arr = result.data;
      arr._seriesKeys = result.seriesKeys;
      arr._labelMap = result.labelMap;
      return arr;
    }

    case 'stacked-bar':
    case 'horizontal-stacked-bar': {
      if (mapping.inputShape === 'wide' && Array.isArray(mapping.valueColumns)) {
        return transformWideCategoryRows(rawData, mapping, 'stack');
      }
      const result = pivotForGrouped(rawData, mapping, 'stack');
      const arr = result.data;
      arr._seriesKeys = result.seriesKeys;
      arr._labelMap = result.labelMap;
      return arr;
    }

    case 'line-chart': {
      if (mapping.inputShape === 'wide' && Array.isArray(mapping.yColumns)) {
        return transformWideSeriesRows(rawData, mapping);
      }
      const xCol = mapping.x;
      const yCol = mapping.y;
      if (!xCol || !yCol) return [];
      const serCol = mapping.series;
      return rawData.map(row => ({
        x: Number(row[xCol]) || 0,
        y: Number(row[yCol]) || 0,
        series: serCol ? String(row[serCol] ?? 'Series 1') : 'Series 1',
      }));
    }

    case 'scatter-plot': {
      const xCol = mapping.x;
      const yCol = mapping.y;
      if (!xCol || !yCol) return [];
      const catCol = mapping.category;
      return rawData.map(row => ({
        x: Number(row[xCol]) || 0,
        y: Number(row[yCol]) || 0,
        category: catCol ? String(row[catCol] ?? 'Data') : 'Data',
      }));
    }

    case 'histogram': {
      const valCol = mapping.value;
      if (!valCol) return [];
      return rawData
        .map(row => Number(row[valCol]))
        .filter(v => !isNaN(v));
    }

    case 'box-plot': {
      const catCol = mapping.category;
      const valCol = mapping.value;
      if (!catCol || !valCol) return [];

      // Group values by category
      const groups = {};
      rawData.forEach(row => {
        const cat = String(row[catCol] ?? '');
        const val = Number(row[valCol]);
        if (isNaN(val)) return;
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(val);
      });

      return Object.entries(groups).map(([category, values]) => {
        const stats = computeBoxStats(values);
        return { category, values, ...stats };
      });
    }

    case 'heatmap': {
      if (mapping.inputShape === 'wide') {
        const rowLabelCol = mapping.rowLabelColumn;
        const valueColumns = Array.isArray(mapping.valueColumns) ? mapping.valueColumns : [];
        if (!rowLabelCol || valueColumns.length === 0) return [];
        const result = [];
        rawData.forEach(row => {
          const yLabel = String(row[rowLabelCol] ?? '');
          valueColumns.forEach(colName => {
            result.push({ x: colName, y: yLabel, value: Number(row[colName]) || 0 });
          });
        });
        return result;
      }
      const xCol = mapping.x;
      const yCol = mapping.y;
      const valCol = mapping.value;
      if (!xCol || !yCol || !valCol) return [];
      return rawData.map(row => ({
        x: String(row[xCol] ?? ''),
        y: String(row[yCol] ?? ''),
        value: Number(row[valCol]) || 0,
      }));
    }

    case 'combo': {
      const catCol = mapping.category;
      const barCol = mapping.barValue;
      const lineCol = mapping.lineValue;
      if (!catCol || !barCol || !lineCol) return [];
      return rawData.map(row => ({
        name: String(row[catCol] ?? ''),
        barValue: Number(row[barCol]) || 0,
        lineValue: Number(row[lineCol]) || 0,
      }));
    }

    case 'radar': {
      const seriesCol = mapping.series;
      if (!seriesCol) return [];
      // Use the configured axis columns, or infer numeric columns across the full dataset.
      const axisCols = Array.isArray(mapping.axisColumns) && mapping.axisColumns.length > 0
        ? mapping.axisColumns
        : inferRadarAxisColumns(rawData, seriesCol);
      if (!axisCols.length) return [];
      return rawData.map(row => ({
        name: String(row[seriesCol] ?? ''),
        values: Object.fromEntries(axisCols.map(col => [col, Number(row[col]) || 0])),
      }));
    }

    default:
      return [];
  }
}

export { computeBoxStats };
